import * as LucideIcons from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { sanitizeSvgMarkup } from "@/utils/svgSanitizer";

interface LucideIconSearchProps {
  onIconSelect: (iconName: string, svgCode: string) => void;
  selectedIcon?: string;
  onClear?: () => void;
  onSvgUpload?: (svgMarkup: string) => void;
}

const LucideIconSearch: React.FC<LucideIconSearchProps> = ({
  onIconSelect,
  selectedIcon,
  onClear,
  onSvgUpload,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const iconTargetSize = 48;
  const defaultExport =
    typeof LucideIcons.default === "object" && LucideIcons.default
      ? (LucideIcons.default as Record<string, unknown>)
      : null;
  const iconsExport =
    typeof (LucideIcons as Record<string, unknown>).icons === "object" &&
    (LucideIcons as Record<string, unknown>).icons
      ? ((LucideIcons as Record<string, unknown>).icons as Record<string, unknown>)
      : null;

  // Build full icon list from lucide-react exports
  const allIconNames = useMemo(() => {
    // Try different ways to access the icons
    let iconNames: string[] = [];

    // Method 1: Direct object keys
    const directKeys = Object.keys(LucideIcons);
    // Method 2: Check if it's a default export with icons property
    if (defaultExport) {
      iconNames = Object.keys(defaultExport)
        .filter((key) => typeof defaultExport[key] === "function" && /^[A-Z]/.test(key))
        .sort((a, b) => a.localeCompare(b));
    }

    // Method 3: Check if icons are directly on the object
    if (iconNames.length === 0) {
      iconNames = directKeys
        .filter((key) => {
          const value = (LucideIcons as Record<string, unknown>)[key];
          return typeof value === "function" && /^[A-Z]/.test(key);
        })
        .sort((a, b) => a.localeCompare(b));
    }

    // Method 4: Try accessing icons property
    if (iconNames.length === 0 && iconsExport) {
      iconNames = Object.keys(iconsExport)
        .filter((key) => typeof iconsExport[key] === "function")
        .sort((a, b) => a.localeCompare(b));
    }

    // Fallback to a comprehensive unique list if still no icons found
    if (iconNames.length === 0) {
      const fallbackIcons = [
        "Activity",
        "Airplay",
        "AlertCircle",
        "AlertOctagon",
        "AlertTriangle",
        "AlignCenter",
        "AlignJustify",
        "AlignLeft",
        "AlignRight",
        "RectangleGoggles",
        "Vr",
        "Glasses",
        "Monitor",
        "Tv",
        "Smartphone",
        "Tablet",
        "Laptop",
        "Desktop",
        "Headphones",
        "Speaker",
        "Anchor",
        "Aperture",
        "Archive",
        "ArrowDown",
        "ArrowDownCircle",
        "ArrowDownLeft",
        "ArrowDownRight",
        "ArrowLeft",
        "ArrowLeftCircle",
        "ArrowRight",
        "ArrowRightCircle",
        "ArrowUp",
        "ArrowUpCircle",
        "ArrowUpLeft",
        "ArrowUpRight",
        "AtSign",
        "Award",
        "BarChart",
        "BarChart2",
        "BarChart3",
        "Battery",
        "BatteryCharging",
        "Bell",
        "BellOff",
        "Bluetooth",
        "Bold",
        "Book",
        "BookOpen",
        "Bookmark",
        "Box",
        "Briefcase",
        "Calendar",
        "Camera",
        "CameraOff",
        "Cast",
        "Check",
        "CheckCircle",
        "CheckSquare",
        "ChevronDown",
        "ChevronLeft",
        "ChevronRight",
        "ChevronUp",
        "Circle",
        "Clipboard",
        "Clock",
        "Cloud",
        "CloudDrizzle",
        "CloudLightning",
        "CloudOff",
        "CloudRain",
        "CloudSnow",
        "Code",
        "Code2",
        "Coffee",
        "Command",
        "Compass",
        "Copy",
        "CreditCard",
        "Crop",
        "Crosshair",
        "Crown",
        "Database",
        "Delete",
        "Disc",
        "DollarSign",
        "Download",
        "DownloadCloud",
        "Droplet",
        "Edit",
        "Edit2",
        "Edit3",
        "ExternalLink",
        "Eye",
        "EyeOff",
        "Facebook",
        "FastForward",
        "Feather",
        "File",
        "FileMinus",
        "FilePlus",
        "FileText",
        "Film",
        "Filter",
        "Flag",
        "Folder",
        "FolderMinus",
        "FolderPlus",
        "Gift",
        "GitBranch",
        "GitCommit",
        "GitMerge",
        "GitPullRequest",
        "Github",
        "Gitlab",
        "Globe",
        "Grid",
        "HardDrive",
        "Hash",
        "Headphones",
        "Heart",
        "HelpCircle",
        "Home",
        "Image",
        "Inbox",
        "Info",
        "Instagram",
        "Italic",
        "Key",
        "Layers",
        "Layout",
        "LifeBuoy",
        "Link",
        "Link2",
        "List",
        "Loader",
        "Lock",
        "LogIn",
        "LogOut",
        "Mail",
        "Map",
        "MapPin",
        "Maximize",
        "Maximize2",
        "Meh",
        "Menu",
        "MessageCircle",
        "MessageSquare",
        "Mic",
        "MicOff",
        "Minimize",
        "Minimize2",
        "Minus",
        "MinusCircle",
        "MinusSquare",
        "Monitor",
        "Moon",
        "MoreHorizontal",
        "MoreVertical",
        "MousePointer",
        "Move",
        "Music",
        "Navigation",
        "Octagon",
        "Package",
        "Paperclip",
        "Pause",
        "PauseCircle",
        "PenTool",
        "Percent",
        "Phone",
        "PhoneCall",
        "PhoneForwarded",
        "PhoneIncoming",
        "PhoneMissed",
        "PhoneOff",
        "PhoneOutgoing",
        "PieChart",
        "Play",
        "PlayCircle",
        "Plus",
        "PlusCircle",
        "PlusSquare",
        "Pocket",
        "Power",
        "Printer",
        "Radio",
        "RefreshCw",
        "Repeat",
        "RotateCcw",
        "RotateCw",
        "Rss",
        "Save",
        "Scissors",
        "Search",
        "Send",
        "Server",
        "Settings",
        "Share",
        "Share2",
        "Shield",
        "ShieldOff",
        "ShoppingBag",
        "ShoppingCart",
        "Shuffle",
        "Sidebar",
        "SkipBack",
        "SkipForward",
        "Slack",
        "Sliders",
        "Smartphone",
        "Smile",
        "Speaker",
        "Square",
        "Star",
        "StopCircle",
        "Sun",
        "Sunrise",
        "Sunset",
        "Tablet",
        "Tag",
        "Target",
        "Terminal",
        "Thermometer",
        "ThumbsDown",
        "ThumbsUp",
        "ToggleLeft",
        "ToggleRight",
        "Tool",
        "Trash",
        "Trash2",
        "Trello",
        "TrendingDown",
        "TrendingUp",
        "Triangle",
        "Truck",
        "Tv",
        "Twitter",
        "Type",
        "Umbrella",
        "Underline",
        "Unlock",
        "Upload",
        "UploadCloud",
        "User",
        "UserCheck",
        "UserMinus",
        "UserPlus",
        "UserX",
        "Users",
        "Video",
        "VideoOff",
        "Voicemail",
        "Volume1",
        "Volume2",
        "VolumeX",
        "Watch",
        "Wifi",
        "WifiOff",
        "Wind",
        "X",
        "XCircle",
        "XSquare",
        "Youtube",
        "Zap",
        "ZoomIn",
        "ZoomOut",
      ];

      // Remove duplicates and sort
      iconNames = [...new Set(fallbackIcons)].sort((a, b) => a.localeCompare(b));
    }

    return iconNames;
  }, []);

  const filteredIcons = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return !q ? allIconNames : allIconNames.filter((name) => name.toLowerCase().includes(q));
  }, [searchQuery, allIconNames]);

  const handleIconClick = (iconName: string) => {
    const IconComponent = getIconComponent(iconName);
    if (!IconComponent) {
      onIconSelect(iconName, "");
      return;
    }
    const svgCode = renderToStaticMarkup(<IconComponent size={24} />);
    onIconSelect(iconName, svgCode);
  };

  // Get the selected icon component
  const getIconComponent = (iconName: string) => {
    let IconComponent = (LucideIcons as Record<string, unknown>)[iconName] as React.ComponentType<{
      size?: number;
    }>;

    if (!IconComponent && defaultExport) {
      IconComponent = defaultExport[iconName] as React.ComponentType<{ size?: number }>;
    }

    if (!IconComponent && iconsExport) {
      IconComponent = iconsExport[iconName] as React.ComponentType<{ size?: number }>;
    }

    return IconComponent;
  };

  const normalizeSvgSize = (svgMarkup: string) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgMarkup, "image/svg+xml");
      const svg = doc.querySelector("svg");
      if (!svg) return svgMarkup;
      const widthRaw = svg.getAttribute("width");
      const heightRaw = svg.getAttribute("height");
      const width = widthRaw ? parseFloat(widthRaw) : NaN;
      const height = heightRaw ? parseFloat(heightRaw) : NaN;
      if (!svg.getAttribute("viewBox")) {
        const viewW = Number.isFinite(width) ? width : iconTargetSize;
        const viewH = Number.isFinite(height) ? height : iconTargetSize;
        svg.setAttribute("viewBox", `0 0 ${viewW} ${viewH}`);
      }
      svg.setAttribute("width", String(iconTargetSize));
      svg.setAttribute("height", String(iconTargetSize));
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      return svg.outerHTML;
    } catch (_error) {
      return svgMarkup;
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label
          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedIcon ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
          onClick={() => {
            if (selectedIcon && onClear) {
              onClear();
            }
          }}
        >
          Lucide Icons
        </Label>
        {selectedIcon && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-gray-400 hover:text-white hover:bg-gray-600 px-2 py-1 rounded"
          >
            Clear
          </button>
        )}
      </div>

      <div className="space-y-2">
        <div className="relative">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={selectedIcon ? selectedIcon : `Search ${allIconNames.length} icons...`}
            className="bg-gray-700 border-gray-600 text-white h-7 text-xs pr-8"
          />
          {selectedIcon && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              {(() => {
                const IconComponent = getIconComponent(selectedIcon);
                return IconComponent ? <IconComponent size={14} className="text-white" /> : null;
              })()}
            </div>
          )}
        </div>
        {onSvgUpload && (
          <div className="relative">
            <input
              type="file"
              accept=".svg,image/svg+xml,.png,image/png"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const isSvg =
                  file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
                if (isSvg) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const result = event.target?.result as string;
                    const normalized = normalizeSvgSize(result);
                    const sanitized = sanitizeSvgMarkup(normalized);
                    if (sanitized) {
                      onSvgUpload(sanitized);
                    }
                  };
                  reader.readAsText(file);
                } else if (file.type === "image/png" || file.name.toLowerCase().endsWith(".png")) {
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = reader.result as string;
                    const img = new Image();
                    img.onload = () => {
                      const width = Math.max(1, Math.round(img.naturalWidth || 1));
                      const height = Math.max(1, Math.round(img.naturalHeight || 1));
                      const svgMarkup = `
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${iconTargetSize}" height="${iconTargetSize}" preserveAspectRatio="xMidYMid meet">
                          <image href="${dataUrl}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet" />
                        </svg>
                      `;
                      const sanitized = sanitizeSvgMarkup(svgMarkup);
                      if (sanitized) {
                        onSvgUpload(sanitized);
                      }
                    };
                    img.src = dataUrl;
                  };
                  reader.readAsDataURL(file);
                }
                e.currentTarget.value = "";
              }}
            />
            <div className="flex items-center justify-between w-full h-6 px-2 text-[10px] bg-gray-700 border border-gray-600 text-white rounded cursor-pointer hover:bg-gray-600 transition-colors">
              <span className="truncate">Upload SVG/PNG icon</span>
              <span className="text-gray-300">Browse</span>
            </div>
          </div>
        )}
        <div className="max-h-32 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2">
          <div className="grid grid-cols-6 gap-1">
            {filteredIcons.map((iconName) => {
              const IconComponent = getIconComponent(iconName);

              if (!IconComponent) {
                return null;
              }

              return (
                <button
                  type="button"
                  key={iconName}
                  onClick={() => handleIconClick(iconName)}
                  className={`p-1 hover:bg-gray-700 rounded text-white flex items-center justify-center transition-colors ${
                    selectedIcon === iconName ? "bg-blue-600" : ""
                  }`}
                  title={iconName}
                >
                  <IconComponent size={16} />
                </button>
              );
            })}
          </div>
          {filteredIcons.length === 0 && searchQuery && (
            <div className="text-center text-gray-400 text-xs py-2">
              No icons found for "{searchQuery}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LucideIconSearch;
